import {inject, Injectable} from '@angular/core';
import {webSocket, WebSocketSubject} from 'rxjs/webSocket';
import {Observable, Subject, throwError} from 'rxjs';
import {PaperChatFilterType, PaperChatWebsocketCreateDTO, PaperChatWebsocketDTO} from '../dto/paper-chat';
import {environment} from '../../../../environments/environment';
import {loadUserLLMModelSettingJSONString} from '../../../utils/models/llm-setting';
import {DeviceIdService} from '../../../utils/services/device-id.service';

@Injectable({
  providedIn: 'root',
})
export class PaperChatWebSocketService {
  private deviceIdService: DeviceIdService = inject(DeviceIdService);
  private socket$: WebSocketSubject<any> | null = null;
  private messageSubject = new Subject<PaperChatWebsocketDTO>();
  private filterType?: PaperChatFilterType;
  private projectId?: number;
  private paperId?: string;

  connect(projectId: number, filterType: PaperChatFilterType, paperId?: string, summaryId?: number): Observable<PaperChatWebsocketDTO> {
    if (!projectId) {
      if (this.projectId && this.filterType !== filterType) {
        console.warn('ProjectId is required, but taken from cache');
      } else {
        return throwError(() => 'ProjectId is required');
      }
    } else {
      this.projectId = projectId;
    }
    if (this.socket$) {
      this.disconnect();
    }
    this.messageSubject = new Subject<PaperChatWebsocketDTO>();
    this.filterType = filterType;
    this.paperId = paperId;
    const filter = this.paperId ? `${PaperChatFilterType.SINGLE}_${this.paperId}` : this.filterType;

    const storedSetting = loadUserLLMModelSettingJSONString();
    const user = this.deviceIdService.getDeviceId() || 'unknown';
    let websocketUrl = `${environment.wsBaseUrl}/paperchat/${this.projectId}?user=${user}&filter=${filter}`;
    if (summaryId) {
      websocketUrl += `&summaryId=${summaryId}`;
    }
    if (storedSetting) {
      const base64: string = btoa(storedSetting);
      websocketUrl += `&llmsetting=${base64}`;
    }

    this.socket$ = webSocket(websocketUrl);

    this.socket$.subscribe({
      next: (message: any) => this.messageSubject.next(message),
      error: (error) => {
        console.error('WebSocket error:', error);
        this.reconnect(projectId);
      },
      complete: () => {
        console.log('WebSocket connection closed');
      },
    });

    return this.messageSubject.asObservable();
  }

  sendMessage(message: string): void {
    if (this.socket$) {
      const sendDto: PaperChatWebsocketCreateDTO = {
        timestamp: Date.now(),
        message: message,
      };
      this.socket$.next(sendDto);
    } else {
      console.error('WebSocket is not connected');
    }
  }

  disconnect(): void {
    if (this.socket$) {
      this.socket$.complete();
      this.socket$.unsubscribe();
      this.socket$ = null;
    }
    this.messageSubject.complete();
  }

  reconnect(projectId: number): void {
    console.log('Reconnecting in 5 seconds...');
    if (this.socket$) {
      this.disconnect();
    }
    if (!this.filterType) {
      return;
    }
    setTimeout(() => this.connect(projectId, this.filterType!, this.paperId), 5000);
  }
}
